/*
  Files tab checklist (UI overhaul wave 2, screen 06 idea 1). Pure helpers.

  REQUIRED_FOLDERS            — the 9 groups / 25 folders every project folder must have (same tree as the
                                backend's REQUIRED_PROJECT_FILE_STRUCTURE, incl. "a. Original Schematic" under
                                "B. Schematic")
  buildFolderChecklist(audit) → rows in display order with status 'ready' | 'empty' | 'missing' | 'not_scanned',
                                file count and OneDrive link, plus counts for the filter
*/

export const REQUIRED_FOLDERS = [
  {
    name: '01. Drawings',
    children: [
      { name: 'A. Received Packages' },
      { name: 'B. Schematic', children: [{ name: 'a. Original Schematic' }] },
      { name: 'c. Wiring Diagrams' },
      { name: 'd. Panels Layout' },
      { name: 'e. Shop drawings by Sara' },
      { name: 'f. Approved Drawings' },
    ],
  },
  { name: '02. Approved Submittal' },
  { name: '03. BOQ & Design', children: [{ name: 'a. Original BOQ' }, { name: 'b. AS-Built BOQ' }, { name: 'c. Variations' }] },
  { name: '04. Programs' },
  { name: '05. Backups' },
  { name: '06. MIR/WIR', children: [{ name: 'a. Delivery Notes & MIRs' }, { name: 'b. WIR' }] },
  { name: '07. Media' },
  { name: '08. Documentations', children: [{ name: 'a. O&M' }, { name: 'b. Warranty' }, { name: 'c. Emails & MOMs' }, { name: 'd. COC' }] },
  { name: '09. Site Details (Word File)' },
];

const countFolders = (nodes = []) => nodes.reduce((sum, node) => sum + 1 + countFolders(node.children || []), 0);
export const REQUIRED_FOLDER_COUNT = countFolders(REQUIRED_FOLDERS);

const key = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

/** Status of one scanned folder node from GET /projects/{id}/files-audit. */
export function folderStatus(node) {
  if (!node) return 'not_scanned';
  if (node.exists === false) return 'missing';
  if (node.is_empty) return 'empty';
  if (node.status === 'missing') return node.exists === true ? 'empty' : 'missing';
  return 'ready';
}

export const FOLDER_STATUS = {
  ready: { label: 'Ready', tone: 'green' },
  empty: { label: 'Empty', tone: 'amber' },
  missing: { label: 'Missing', tone: 'red' },
  not_scanned: { label: 'Not scanned', tone: 'gray' },
};

/**
 * Every required folder in display order, merged with the scan (when there is one).
 * → { rows: [{ id, name, path, depth, isGroup, status, fileCount, webUrl, summary }], counts: { all, ready, empty,
 *     missing, attention, notScanned }, scanned }
 */
export function buildFolderChecklist(audit) {
  const scanned = Boolean(audit && Array.isArray(audit.structure));
  const rows = [];
  const counts = { all: 0, ready: 0, empty: 0, missing: 0, attention: 0, notScanned: 0 };

  const visit = (required, scannedNodes, depth, parentPath, parentMissing) => {
    required.forEach((folder) => {
      const node = (scannedNodes || []).find((entry) => key(entry?.name) === key(folder.name)) || null;
      const path = parentPath ? `${parentPath}/${folder.name}` : folder.name;
      let status = 'not_scanned';
      if (scanned) status = parentMissing ? 'missing' : folderStatus(node);
      counts.all += 1;
      if (status === 'not_scanned') counts.notScanned += 1;
      else counts[status] += 1;
      if (status === 'empty' || status === 'missing') counts.attention += 1;
      const row = {
        id: path,
        name: folder.name,
        path,
        depth,
        isGroup: depth === 0,
        hasChildren: Boolean(folder.children?.length),
        status,
        fileCount: node && status !== 'missing' ? Number(node.direct_file_count || 0) : null,
        webUrl: node?.web_url || '',
        reason: node?.missing_reason || '',
      };
      rows.push(row);
      if (folder.children?.length) {
        const start = rows.length;
        visit(folder.children, node?.children || [], depth + 1, path, status === 'missing');
        const children = rows.slice(start);
        row.summary = {
          folders: children.length,
          empty: children.filter((child) => child.status === 'empty').length,
          missing: children.filter((child) => child.status === 'missing').length,
        };
      }
    });
  };
  visit(REQUIRED_FOLDERS, scanned ? audit.structure : [], 0, '', false);
  return { rows, counts, scanned };
}

/** Rows for a filter: 'all' | 'attention' (Empty or Missing, with their group headers) | 'ready'. */
export function filterFolderRows(rows = [], filter = 'all') {
  if (filter === 'all') return rows;
  const wanted = (row) => (filter === 'ready' ? row.status === 'ready' : row.status === 'empty' || row.status === 'missing');
  const keep = new Set();
  rows.forEach((row, index) => {
    if (!wanted(row)) return;
    keep.add(index);
    // keep the parents so a row is never shown without its group
    for (let back = index - 1, depth = row.depth; back >= 0 && depth > 0; back -= 1) {
      if (rows[back].depth < depth) {
        keep.add(back);
        depth = rows[back].depth;
      }
    }
  });
  return rows.filter((row, index) => keep.has(index));
}

/* ---------------------------------------------------------------- wave 3 (screen 06 ideas 4, 5 and 7) */

/**
 * 06 #5: the stage each main folder proves. A stage line whose name matches a sub-folder ("C. Variations" ↔
 * "c. Variations") belongs to that sub-folder.
 */
export const FOLDER_STAGE_LINKS = [
  { folder: '01. Drawings', stage: 'Received Drawings' },
  { folder: '02. Approved Submittal', stage: 'Material Submittal' },
  { folder: '03. BOQ & Design', stage: 'BOQ & Design' },
  { folder: '08. Documentations', stage: 'Handing over documents' },
];

/** 'c. Variations' / 'C. Variations' / 'Variations' → 'variations' */
export const folderLabelKey = (value) => key(value)
  .replace(/^\s*(?:\d+|[a-z])[.)]\s*/i, '')
  .replace(/&/g, ' and ')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

/** The stage linked to a main folder (null when none, or when the person cannot see it). */
export function linkedStageForFolder(folderName, stages = []) {
  const link = FOLDER_STAGE_LINKS.find((entry) => key(entry.folder) === key(folderName));
  if (!link) return null;
  const index = (stages || []).findIndex((stage) => key(stage?.name) === key(link.stage));
  if (index < 0) return null;
  const stage = stages[index];
  return { stage, number: String(Number(stage.order) || index + 1).padStart(2, '0') };
}

/** The required folder a stage line belongs to: the sub-folder with the same name, else the stage's folder. */
export function folderForStageLine(stageName, lineName, rows = []) {
  const link = FOLDER_STAGE_LINKS.find((entry) => key(entry.stage) === key(stageName));
  if (!link) return null;
  const group = rows.find((row) => row.isGroup && key(row.name) === key(link.folder));
  if (!group) return null;
  const wanted = folderLabelKey(lineName);
  const child = wanted ? rows.find((row) => row.path.startsWith(`${group.path}/`) && folderLabelKey(row.name) === wanted) : null;
  return child || group;
}

/** Folder rows under a main folder (all depths). */
const childrenOf = (rows, group) => rows.filter((row) => row.path.startsWith(`${group.path}/`));

/**
 * 06 #5 "Stage evidence": each linked stage with its status and how complete its folder is. `warn` = the stage
 * is approved (or its work is shown as done) but its folder still has empty / missing folders.
 * → [{ stage, number, folder, folderPath, ready, total, empty, missing, note, tone, warn }]
 */
export function buildStageEvidence(stages = [], checklist) {
  const rows = checklist?.rows || [];
  return FOLDER_STAGE_LINKS.map((link) => {
    const linked = linkedStageForFolder(link.folder, stages);
    const group = rows.find((row) => row.isGroup && key(row.name) === key(link.folder));
    if (!linked || !group) return null;
    const folders = [group, ...childrenOf(rows, group)];
    const counted = group.hasChildren ? folders.slice(1) : folders;
    const scanned = Boolean(checklist?.scanned);
    const ready = counted.filter((row) => row.status === 'ready').length;
    const weak = counted.filter((row) => row.status === 'empty' || row.status === 'missing');
    const empty = weak.filter((row) => row.status === 'empty');
    const missing = weak.filter((row) => row.status === 'missing');
    let note = 'Not scanned yet';
    let tone = 'gray';
    if (scanned) {
      if (group.status === 'missing') { note = 'Folder missing'; tone = 'red'; }
      else if (!weak.length) { note = group.hasChildren ? `${ready} of ${counted.length} ready` : 'Ready'; tone = 'green'; }
      else if (weak.length === 1) { note = `${weak[0].name} ${weak[0].status}`; tone = weak[0].status === 'missing' ? 'red' : 'amber'; }
      else {
        note = [empty.length ? `${empty.length} empty` : '', missing.length ? `${missing.length} missing` : ''].filter(Boolean).join(' · ');
        tone = missing.length ? 'red' : 'amber';
      }
    }
    return {
      stage: linked.stage,
      number: linked.number,
      folder: link.folder,
      folderPath: group.path,
      ready,
      total: counted.length,
      empty: empty.length,
      missing: missing.length,
      note,
      tone,
      warn: scanned && weak.length > 0 && ['Approved', 'Completed'].includes(String(linked.stage.status || '')),
    };
  }).filter(Boolean);
}

/** Folder health for the ring (06 #4): { total, ready, empty, missing, attention, percent } from a checklist. */
export function folderHealth(checklist) {
  const counts = checklist?.counts || {};
  const total = counts.all || REQUIRED_FOLDER_COUNT;
  return {
    total,
    ready: counts.ready || 0,
    empty: counts.empty || 0,
    missing: counts.missing || 0,
    attention: counts.attention || 0,
    percent: total ? Math.round(((counts.ready || 0) / total) * 100) : 0,
  };
}

/** Upload / browse rules shown under the drop zone (same list as the server's UPLOAD_EXTENSIONS). */
export const FOLDER_UPLOAD_ACCEPT = '.pdf,.dwg,.dxf,.xlsx,.xls,.xlsm,.csv,.docx,.doc,.pptx,.txt,.zip,.jpg,.jpeg,.png,.webp,.heic,.mp4,.mov';
export const FOLDER_UPLOAD_MAX_BYTES = 50 * 1024 * 1024;

/** 'PDF · 412 KB' style file type label from a name / mime type. */
export function fileKindLabel(name = '', mime = '') {
  const extension = String(name).split('.').pop();
  if (extension && extension !== name && extension.length <= 5) return extension.toUpperCase();
  if (mime) return mime.split('/').pop().toUpperCase();
  return 'File';
}
